/**
 * AutoTravelHUD — camera-fixed auto-travel panel for Round 9 Phase B.
 *
 * Visible only while the local player is in an active auto-travel phase
 * (preparing, traveling, or paused). Renders:
 *   - Destination name
 *   - Progress bar (waypoints completed / total)
 *   - ETA countdown (from server `estimatedArrivalAt`)
 *   - Phase indicator (Preparing… / Traveling… / Paused)
 *   - Stop button hint ("Press any key or tap Stop")
 *
 * Two layers (mirrors ExpeditionCampHUD pattern):
 *   1. **`AutoTravelHudState`** — pure non-Phaser state class.
 *   2. **`AutoTravelHUD`** — Phaser shell that owns render objects.
 *
 * Camera-fixed (`setScrollFactor(0)`); high depth so it always sits over
 * world geometry.
 */

import * as Phaser from 'phaser';
import type {
  AutoTravelStateEvent,
  AutoTravelDestinationsEvent,
  AutoTravelPhase,
} from '@game/shared';
import {
  MSG_AUTO_TRAVEL_STATE,
  MSG_AUTO_TRAVEL_DESTINATIONS,
  MSG_AUTO_TRAVEL_CANCEL,
  AUTO_TRAVEL_PREPARE_MS,
} from '@game/shared';
import { FontManager } from './FontManager';
import {
  HUD_BG,
  HUD_BORDER,
  HUD_TEXT_SECONDARY,
  PAL_OFF_WHITE,
  PAL_GREEN,
  PAL_YELLOW,
  PAL_MID_GRAY,
  hexToCSS,
} from './palette';

const HUD_DEPTH = 1001;

// Layout — top-center, slightly below top edge.
const PANEL_W = 220;
const PANEL_H = 64;
const PANEL_PAD_X = 10;
const PANEL_PAD_Y = 6;
const BAR_W = 140;
const BAR_H = 8;
const BAR_Y_OFFSET = 36;

// Destination picker layout
const PICKER_W = 240;
const PICKER_ROW_H = 28;
const PICKER_PAD = 8;

// ── Realm display names (shared with LeyLineRegistry) ──

const NODE_DISPLAY: Record<string, string> = {
  'hearth:verdana_fields': 'Donchitos Hamlet',
  'hearth:sablewind_reaches': 'Dune-Edge Caravanserai',
  'hearth:frostmere_expanse': 'Hearthmere Hold',
  'hearth:ember_mire': 'Ashvein Market',
  'door:verdana_fields': 'Verdana Expedition',
  'door:sablewind_reaches': 'Sablewind Expedition',
  'door:frostmere_expanse': 'Frostmere Expedition',
  'door:ember_mire': 'Ember Expedition',
};

function nodeDisplayName(nodeId: string, fallback?: string): string {
  return NODE_DISPLAY[nodeId] ?? fallback ?? nodeId;
}

function formatMs(ms: number): string {
  if (ms <= 0) return '0s';
  const secs = Math.ceil(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// ── Pure state class ──────────────────────────────────────────────────────

/**
 * Pure state for the auto-travel HUD. No Phaser dependency.
 */
export class AutoTravelHudState {
  private phase: AutoTravelPhase = 'idle';
  private originNodeId = '';
  private destinationNodeId = '';
  private currentWaypointIndex = 0;
  private totalWaypoints = 0;
  private estimatedArrivalAt = 0;
  private receivedAt = 0;
  private prepareStartedAt = 0;

  apply(payload: unknown, now: number = Date.now()): void {
    if (!payload || typeof payload !== 'object') return;
    const p = payload as Partial<AutoTravelStateEvent>;
    if (typeof p.phase !== 'string') return;
    this.phase = p.phase as AutoTravelPhase;
    this.originNodeId = p.originNodeId ?? '';
    this.destinationNodeId = p.destinationNodeId ?? '';
    this.currentWaypointIndex = p.currentWaypointIndex ?? 0;
    this.totalWaypoints = p.totalWaypoints ?? 0;
    this.estimatedArrivalAt = p.estimatedArrivalAt ?? 0;
    this.receivedAt = now;
    if (this.phase === 'preparing' && this.prepareStartedAt === 0) {
      this.prepareStartedAt = now;
    }
    if (this.phase !== 'preparing') {
      this.prepareStartedAt = 0;
    }
  }

  isVisible(): boolean {
    return this.phase === 'preparing' || this.phase === 'traveling' || this.phase === 'paused';
  }

  isActive(): boolean {
    return this.phase !== 'idle' && this.phase !== 'arrived' && this.phase !== 'cancelled';
  }

  getPhase(): AutoTravelPhase {
    return this.phase;
  }

  getDestinationName(): string {
    return nodeDisplayName(this.destinationNodeId);
  }

  getProgress(): { current: number; total: number; percent: number } {
    const total = Math.max(1, this.totalWaypoints);
    const current = Math.min(this.currentWaypointIndex, total);
    return { current, total, percent: (current / total) * 100 };
  }

  getEtaMs(now: number = Date.now()): number {
    if (this.phase === 'preparing') {
      const elapsed = now - this.prepareStartedAt;
      return Math.max(0, AUTO_TRAVEL_PREPARE_MS - elapsed);
    }
    if (this.phase === 'paused') return -1; // unknown
    return Math.max(0, this.estimatedArrivalAt - now);
  }

  getPhaseLabel(now: number = Date.now()): string {
    switch (this.phase) {
      case 'preparing': {
        const remaining = this.getEtaMs(now);
        return `Preparing... ${formatMs(remaining)}`;
      }
      case 'traveling': {
        const remaining = this.getEtaMs(now);
        return `Traveling... ${formatMs(remaining)}`;
      }
      case 'paused':
        return 'Paused';
      default:
        return '';
    }
  }

  clear(): void {
    this.phase = 'idle';
    this.originNodeId = '';
    this.destinationNodeId = '';
    this.currentWaypointIndex = 0;
    this.totalWaypoints = 0;
    this.estimatedArrivalAt = 0;
    this.receivedAt = 0;
    this.prepareStartedAt = 0;
  }

  getLastReceivedAt(): number {
    return this.receivedAt;
  }
}

// ── Room interface ────────────────────────────────────────────────────────

export interface AutoTravelRoomLike {
  onMessage(type: string, cb: (payload: unknown) => void): void;
  send(type: string, data?: unknown): void;
}

// ── Destination picker callback ──────────────────────────────────────────

export type DestinationPickerCallback = (nodeId: string) => void;

// ── Phaser shell ──────────────────────────────────────────────────────────

export class AutoTravelHUD {
  private scene: Phaser.Scene;
  private state = new AutoTravelHudState();
  private room: AutoTravelRoomLike | null = null;

  // Travel progress panel
  private container: Phaser.GameObjects.Container;
  private bg: Phaser.GameObjects.Rectangle;
  private border: Phaser.GameObjects.Rectangle;
  private destLabel: Phaser.GameObjects.Text;
  private phaseLabel: Phaser.GameObjects.Text;
  private progressBg: Phaser.GameObjects.Rectangle;
  private progressFill: Phaser.GameObjects.Rectangle;
  private stopHint: Phaser.GameObjects.Text;

  // Destination picker (overlay list)
  private pickerContainer: Phaser.GameObjects.Container | null = null;
  private pickerItems: Phaser.GameObjects.Text[] = [];
  private pickerBg: Phaser.GameObjects.Rectangle | null = null;
  private pickerTitle: Phaser.GameObjects.Text | null = null;
  private destinations: AutoTravelDestinationsEvent['destinations'] = [];
  private onPickDestination: DestinationPickerCallback | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Center-top placement
    const x = Math.floor(scene.scale.width / 2 - PANEL_W / 2);
    const y = 8;

    this.container = scene.add.container(x, y);
    this.container.setScrollFactor(0);
    this.container.setDepth(HUD_DEPTH);
    (scene as any).addToUI?.(this.container);

    this.bg = scene.add.rectangle(0, 0, PANEL_W, PANEL_H, HUD_BG, 0.9);
    this.bg.setOrigin(0, 0);
    this.border = scene.add.rectangle(0, 0, PANEL_W, PANEL_H);
    this.border.setOrigin(0, 0);
    this.border.setStrokeStyle(1, HUD_BORDER, 1);
    this.border.setFillStyle(0, 0);
    this.container.add([this.bg, this.border]);

    this.destLabel = scene.add.text(PANEL_PAD_X, PANEL_PAD_Y, '', {
      fontFamily: FontManager.fontFamily('body'),
      fontSize: '13px',
      color: hexToCSS(PAL_OFF_WHITE),
    });
    this.container.add(this.destLabel);

    this.phaseLabel = scene.add.text(PANEL_PAD_X, PANEL_PAD_Y + 16, '', {
      fontFamily: FontManager.fontFamily('body'),
      fontSize: '11px',
      color: hexToCSS(PAL_YELLOW),
    });
    this.container.add(this.phaseLabel);

    // Progress bar
    const barX = PANEL_PAD_X;
    const barY = BAR_Y_OFFSET;
    this.progressBg = scene.add.rectangle(barX, barY, BAR_W, BAR_H, PAL_MID_GRAY, 1);
    this.progressBg.setOrigin(0, 0);
    this.progressFill = scene.add.rectangle(barX, barY, BAR_W, BAR_H, PAL_GREEN, 1);
    this.progressFill.setOrigin(0, 0);
    this.container.add([this.progressBg, this.progressFill]);

    // Stop hint
    this.stopHint = scene.add.text(PANEL_PAD_X + BAR_W + 8, barY - 2, 'Stop', {
      fontFamily: FontManager.fontFamily('body'),
      fontSize: '11px',
      color: hexToCSS(HUD_TEXT_SECONDARY),
    });
    this.stopHint.setInteractive({ useHandCursor: true });
    this.stopHint.on('pointerdown', () => this.cancelTravel());
    this.container.add(this.stopHint);

    // Waypoint counter to the right of progress bar
    this.container.setVisible(false);
  }

  /** Wire onMessage subscriptions. Call once after the room is established. */
  attach(room: AutoTravelRoomLike): void {
    this.room = room;

    room.onMessage(MSG_AUTO_TRAVEL_STATE, (payload) => {
      this.state.apply(payload);
      this.refresh();
    });

    room.onMessage(MSG_AUTO_TRAVEL_DESTINATIONS, (payload) => {
      const p = payload as AutoTravelDestinationsEvent;
      if (p && Array.isArray(p.destinations)) {
        this.showPicker(p);
      }
    });
  }

  /** Per-frame update — countdown timers. */
  update(): void {
    if (!this.state.isVisible()) return;
    const now = Date.now();
    this.phaseLabel.setText(this.state.getPhaseLabel(now));
  }

  /** Cancel auto-travel via room message. */
  cancelTravel(): void {
    this.room?.send(MSG_AUTO_TRAVEL_CANCEL, {});
  }

  /** Returns true if auto-travel is active (for input cancellation check). */
  isActive(): boolean {
    return this.state.isActive();
  }

  /** Returns the phase for external systems (e.g. camera follow). */
  getPhase(): AutoTravelPhase {
    return this.state.getPhase();
  }

  // ── Destination picker ──

  showPicker(event: AutoTravelDestinationsEvent): void {
    this.hidePicker();
    this.destinations = event.destinations;
    if (this.destinations.length === 0) return;

    const cx = Math.floor(this.scene.scale.width / 2 - PICKER_W / 2);
    const cy = Math.floor(this.scene.scale.height / 2 - ((this.destinations.length + 1) * PICKER_ROW_H + PICKER_PAD * 2) / 2);

    this.pickerContainer = this.scene.add.container(cx, cy);
    this.pickerContainer.setScrollFactor(0);
    this.pickerContainer.setDepth(HUD_DEPTH + 10);
    (this.scene as any).addToUI?.(this.pickerContainer);

    const totalH = (this.destinations.length + 1) * PICKER_ROW_H + PICKER_PAD * 2;
    this.pickerBg = this.scene.add.rectangle(0, 0, PICKER_W, totalH, HUD_BG, 0.95);
    this.pickerBg.setOrigin(0, 0);
    this.pickerBg.setStrokeStyle(1, HUD_BORDER, 1);
    this.pickerContainer.add(this.pickerBg);

    this.pickerTitle = this.scene.add.text(PICKER_PAD, PICKER_PAD, 'Select Destination', {
      fontFamily: FontManager.fontFamily('body'),
      fontSize: '13px',
      color: hexToCSS(PAL_YELLOW),
    });
    this.pickerContainer.add(this.pickerTitle);

    this.pickerItems = [];
    for (let i = 0; i < this.destinations.length; i++) {
      const d = this.destinations[i];
      const etaStr = formatMs(d.estimatedTravelMs);
      const label = `${d.displayName}  (${d.leyDustCost} dust, ~${etaStr})`;
      const y = PICKER_PAD + (i + 1) * PICKER_ROW_H;
      const text = this.scene.add.text(PICKER_PAD, y, label, {
        fontFamily: FontManager.fontFamily('body'),
        fontSize: '12px',
        color: hexToCSS(PAL_OFF_WHITE),
      });
      text.setInteractive({ useHandCursor: true });
      text.on('pointerover', () => text.setColor(hexToCSS(PAL_GREEN)));
      text.on('pointerout', () => text.setColor(hexToCSS(PAL_OFF_WHITE)));
      text.on('pointerdown', () => {
        this.selectDestination(d.nodeId);
      });
      this.pickerContainer.add(text);
      this.pickerItems.push(text);
    }
  }

  hidePicker(): void {
    this.pickerContainer?.destroy();
    this.pickerContainer = null;
    this.pickerItems = [];
    this.pickerBg = null;
    this.pickerTitle = null;
    this.destinations = [];
  }

  /** Register a callback for when a destination is picked. */
  setOnPickDestination(cb: DestinationPickerCallback | null): void {
    this.onPickDestination = cb;
  }

  private selectDestination(nodeId: string): void {
    this.hidePicker();
    if (this.onPickDestination) {
      this.onPickDestination(nodeId);
    }
  }

  // ── Refresh render state ──

  private refresh(): void {
    const visible = this.state.isVisible();
    this.container.setVisible(visible);

    if (!visible) {
      // If travel ended, hide picker too
      if (!this.state.isActive()) {
        this.hidePicker();
      }
      return;
    }

    const now = Date.now();
    this.destLabel.setText(this.state.getDestinationName());
    this.phaseLabel.setText(this.state.getPhaseLabel(now));

    const progress = this.state.getProgress();
    this.progressFill.setScale(progress.percent / 100, 1);
  }

  /** Tear-down. */
  destroy(): void {
    this.container.destroy();
    this.hidePicker();
    this.state.clear();
  }

  /** Test access. */
  getState(): AutoTravelHudState {
    return this.state;
  }
}
